import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import PasswordInput from "../components/PasswordInput";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ClientLogin() {
  const { loginWithCredentials, resendVerification, apiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // v1.36: when the backend refuses login because the account isn't
  // verified, we surface a specific banner + one-click resend instead
  // of a scary generic toast.
  const [unverifiedFor, setUnverifiedFor] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverifiedFor("");
    try {
      const user = await loginWithCredentials(email.trim(), password);
      toast.success(`Benvenuto ${user.name_salone || user.email}`);
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns {code: "EMAIL_NOT_VERIFIED", email, message}
      if (detail && typeof detail === "object" && detail.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedFor(detail.email || email.trim());
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification(unverifiedFor);
      toast.success("Ti abbiamo rimandato il link di conferma");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <AuthShell
      side="client"
      title="Accedi — Cliente"
      subtitle="Entra con le credenziali del tuo salone."
      altLinkLabel="Non hai un account? Registrati →"
      altLinkTo="/cliente/registrati"
      altLinkDataTestId="client-login-to-register"
    >
      <form onSubmit={onSubmit} data-testid="client-login-form" className="space-y-5">
        {unverifiedFor && (
          <div
            data-testid="client-login-unverified-banner"
            className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <div className="font-semibold">Email non ancora confermata</div>
            <p className="mt-1 text-xs leading-relaxed">
              Devi cliccare il link che ti abbiamo mandato per attivare il tuo
              account. Controlla la posta (anche <strong>spam</strong>) oppure
              richiedi un nuovo link qui sotto.
            </p>
            <button
              type="button"
              onClick={handleResend}
              data-testid="client-login-resend-verification"
              className="mt-2 text-xs font-bold text-[var(--mp-ink)] underline"
            >
              Rimandami il link a {unverifiedFor}
            </button>
          </div>
        )}
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em]">
            Email
          </Label>
          <Input
            id="email"
            data-testid="client-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
            placeholder="salone@email.it"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em]">
              Password
            </Label>
            <Link
              to="/password-dimenticata"
              data-testid="client-login-forgot"
              className="text-xs font-semibold text-[var(--mp-muted)] hover:text-[var(--mp-ink)] underline underline-offset-2"
            >
              Password dimenticata?
            </Link>
          </div>
          <PasswordInput
            id="password"
            dataTestId="client-login-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
            placeholder="••••••••"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="client-login-submit"
          className="w-full h-12 rounded-full bg-[var(--mp-primary)] hover:bg-[var(--mp-primary-hover)] text-white"
        >
          {loading ? "Accesso in corso…" : "Accedi"}
        </Button>
      </form>
    </AuthShell>
  );
}
